import * as React from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import { select, line, SeriesPoint, transition } from 'd3';
import useStackedArea from './useStackedArea';
import clsx from 'clsx';

export type StackedAreaDatum = {
  /** Properties corresponding to those in the data key. Additional
   * keys not in the dataKey will be ignored */
  [key: string]: number;
};

/**
 * Name and color key for data provided in the Stacked Area Datum.
 */
export type StackedAreaDataKey = {
  [Property in keyof StackedAreaDatum]: [string, string];
};
export interface StackedAreaProps {
  /** viewBox width */
  viewBoxWidth: number;
  /** viewBox height */
  viewBoxHeight: number;
  /** Array of data objects with a key for each area */
  data: StackedAreaDatum[];
  /** for each key in the data, two colors: one for the area and one for the line */
  dataKey: StackedAreaDataKey;
  /** className for div container overrides */
  containerClassName?: string;
  svgWidth?: number | string;
  svgHeight?: number | string;
}

const useStyles = makeStyles<Theme, { svgWidth: number | string; svgHeight: number | string }>(
  (theme: Theme) => ({
    root: {
      display: 'flex',
      width: '100%',
      height: '100%'
    },
    svg: ({ svgHeight, svgWidth }) => ({
      fontFamily: 'Open Sans',
      height: svgHeight,
      width: svgWidth
    }),
    lines: {
      fill: 'none',
      strokeWidth: 1,
      strokeLinejoin: 'round',
      strokeLinecap: 'round'
    }
  })
);

const StackedArea = ({
  viewBoxWidth,
  viewBoxHeight,
  data,
  dataKey,
  containerClassName,
  svgHeight,
  svgWidth
}: StackedAreaProps) => {
  const classes = useStyles({ svgHeight, svgWidth });
  const areasRef = React.useRef<SVGGElement>();
  const linesRef = React.useRef<SVGGElement>();
  const axisRef = React.useRef<SVGGElement>();
  const { stackSeries, areaGen, x, y, yAxis } = useStackedArea(
    viewBoxWidth,
    viewBoxHeight,
    data,
    dataKey
  );
  const getTransition = () => transition().duration(500);

  React.useEffect(() => {
    select(areasRef.current)
      .selectAll('path')
      .data(stackSeries)
      .join(
        (enter) =>
          enter
            .append('path')
            .attr('fill', ({ key }) => dataKey[key][0])
            .attr('d', areaGen),
        (update) => update.call(() => update.transition(getTransition()).attr('d', areaGen))
      );
  }, [areasRef, stackSeries]);

  React.useEffect(() => {
    select(linesRef.current)
      .selectAll('path')
      .data(stackSeries)
      .join(
        (enter) =>
          enter
            .append('path')
            .attr('stroke', (d) => dataKey[d.key][1])
            .attr(
              'd',
              line<SeriesPoint<{ [key: string]: number }>>()
                .x((d, i) => x(i))
                .y((d) => y(d[1]))
            ),
        (update) =>
          update.call(() =>
            update.transition(getTransition()).attr(
              'd',
              line<SeriesPoint<{ [key: string]: number }>>()
                .x((d, i) => x(i))
                .y((d) => y(d[1]))
            )
          )
      );
  }, [linesRef, stackSeries]);

  React.useEffect(() => {
    select(axisRef.current).call(yAxis);
  }, [axisRef, stackSeries]);

  return (
    <div className={clsx(classes.root, containerClassName)}>
      <svg className={classes.svg} viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}>
        <g ref={areasRef}></g>
        <g className={classes.lines} ref={linesRef}></g>
        <g ref={axisRef}></g>
      </svg>
    </div>
  );
};

export default StackedArea;
